/* global axios */
import ApiClient from './ApiClient';
import { requestConfig, resourcePath } from './lais/httpHelpers';
import { redactLaisPayload } from './lais/redaction';
import { normalizeLaisContact } from './laisCrmContacts';

const OPERATIONAL_PAYLOAD_KEYS = [
  'raw_payload',
  'rawPayload',
  'request_payload',
  'requestPayload',
  'response_payload',
  'responsePayload',
  'payload',
  'metadata',
];

const isObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const redactOperationalPayloads = item => {
  if (!isObject(item)) return {};

  const normalized = { ...item };
  OPERATIONAL_PAYLOAD_KEYS.forEach(key => {
    if (normalized[key] !== undefined) {
      normalized[key] = redactLaisPayload(normalized[key]);
    }
  });

  return normalized;
};

const normalizeFavoriteItem = item => {
  const normalized = redactOperationalPayloads(item);

  if (isObject(normalized.contact)) {
    normalized.contact = normalizeLaisContact(normalized.contact);
  }
  if (isObject(normalized.imovel)) {
    normalized.imovel = redactOperationalPayloads(normalized.imovel);
  }

  return normalized;
};

export const normalizeFavoriteList = list => {
  const normalized = redactOperationalPayloads(list);

  if (isObject(normalized.contact)) {
    normalized.contact = normalizeLaisContact(normalized.contact);
  }
  if (isObject(normalized.cover_favorite)) {
    normalized.cover_favorite = normalizeFavoriteItem(
      normalized.cover_favorite
    );
  }
  if (Array.isArray(normalized.favorites)) {
    normalized.favorites = normalized.favorites.map(normalizeFavoriteItem);
  }

  return normalized;
};

export const normalizeFavoriteListsPayload = data => {
  if (Array.isArray(data)) return data.map(normalizeFavoriteList);
  if (!isObject(data)) return data;

  const normalized = { ...data };
  if (Array.isArray(data.data)) {
    normalized.data = data.data.map(normalizeFavoriteList);
  }
  if (Array.isArray(data.payload)) {
    normalized.payload = data.payload.map(normalizeFavoriteList);
  }

  return normalized;
};

const withNormalizedFavoriteLists = request =>
  request.then(response => ({
    ...response,
    data: normalizeFavoriteListsPayload(response.data),
  }));

const withNormalizedFavoriteList = request =>
  request.then(response => ({
    ...response,
    data: normalizeFavoriteList(response.data),
  }));

class FavoriteListsAPI extends ApiClient {
  constructor() {
    super('favorite_lists', { accountScoped: true });
  }

  list({ contactId } = {}) {
    return withNormalizedFavoriteLists(
      axios.get(this.url, requestConfig({ contact_id: contactId }))
    );
  }

  show(id) {
    return withNormalizedFavoriteList(
      axios.get(resourcePath(this.url, id), requestConfig())
    );
  }

  create({ contactId, name }) {
    return withNormalizedFavoriteList(
      axios.post(
        this.url,
        {
          contact_id: contactId,
          name,
        },
        requestConfig()
      )
    );
  }

  update(id, { name }) {
    return withNormalizedFavoriteList(
      axios.patch(resourcePath(this.url, id), { name }, requestConfig())
    );
  }

  destroy(id) {
    return axios.delete(resourcePath(this.url, id), requestConfig());
  }
}

export default new FavoriteListsAPI();
